/**
 * Routes. `/sign-in` and the personal capture link `/c/{user}-{token}` stand alone (the link page has no session:
 * ADR-001 §10.2); everything else needs a session and renders inside the shell from GET /me. A 401 anywhere sends
 * the user to `/sign-in?redirect=<where they were>`.
 */
import { useEffect } from 'react';
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { ApiFailure, setMustChangeHandler, setNoSessionHandler } from './api';
import { ME_KEY, SessionProvider, useMeQuery } from './lib/session';
import { AppShell } from './shell/AppShell';
import { Page } from './shell/Page';
import { SignIn } from './screens/SignIn';
import { ChangePassword } from './screens/ChangePassword';
import { CaptureLink, CaptureLinkRoute } from './screens/capture/CaptureLink';
import { ToastProvider, errorText } from './ui/kit';

function RequireSession() {
  const me = useMeQuery();
  const loc = useLocation();
  const nav = useNavigate();
  const qc = useQueryClient();

  useEffect(() => {
    setNoSessionHandler(() => {
      if (window.location.pathname.startsWith('/sign-in')) return;
      qc.removeQueries();
      const here = window.location.pathname + window.location.search;
      nav(`/sign-in${here && here !== '/' ? `?redirect=${encodeURIComponent(here)}` : ''}`, { replace: true });
    });
    // Any 403 must_change_password (e.g. a reset while this tab was open): read /me again, which shows the gate.
    setMustChangeHandler(() => void qc.invalidateQueries({ queryKey: ME_KEY }));
  }, [nav, qc]);

  if (me.isPending) return <Boot />;
  if (me.isError) {
    if (me.error instanceof ApiFailure && me.error.status === 401) {
      const here = loc.pathname + loc.search;
      return <Navigate to={`/sign-in${here && here !== '/' ? `?redirect=${encodeURIComponent(here)}` : ''}`} replace />;
    }
    return <BootError message={errorText(me.error)} onRetry={() => me.refetch()} />;
  }
  // Signed in with a temporary password: nothing else works until it is changed (every other route answers 403).
  if (me.data.must_change_password) return <ChangePassword me={me.data} />;
  return (
    <SessionProvider me={me.data}>
      <Outlet />
    </SessionProvider>
  );
}

function Home() {
  const me = useMeQuery();
  return me.data ? <Navigate to={me.data.home_route} replace /> : null;
}

/** First paint while /me loads: the bar and an empty menu in the company colours, so nothing jumps. */
function Boot() {
  return (
    <>
      <header className="top">
        <div className="logo">
          <div className="glyph">
            <i />
            <i />
            <i />
            <i />
          </div>
          <div>
            <div className="lg1">Amplispot</div>
            <div className="lg2">Ops</div>
          </div>
        </div>
      </header>
      <div className="shell">
        <nav className="side" aria-busy="true" />
        <main className="main">
          <div className="inner">
            <div className="empty loading">
              <span className="spin" aria-hidden="true" />
              Loading your workspace…
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

function BootError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="signin">
      <div className="signin-card">
        <h1>Amplispot Ops did not load</h1>
        <div className="note bad">{message}</div>
        <button className="btn pri" onClick={onRetry}>
          Try again
        </button>
      </div>
    </div>
  );
}

/** Links issued before the `/c/` form were `/capture?t=<token>`: with a token that is the link page, not the app. */
function SessionOrLink() {
  const loc = useLocation();
  const t = loc.pathname.replace(/\/$/, '') === '/capture' ? new URLSearchParams(loc.search).get('t') : null;
  return t ? <CaptureLink token={t} /> : <RequireSession />;
}

const router = createBrowserRouter([
  { path: '/sign-in', element: <SignIn /> },
  { path: '/c/:link', element: <CaptureLinkRoute /> },
  {
    element: <SessionOrLink />,
    children: [
      { index: true, element: <Home /> },
      {
        element: <AppShell />,
        children: [
          { path: ':item', element: <Page /> },
          { path: ':item/:tab', element: <Page /> },
          { path: ':item/:tab/:sub', element: <Page /> },
        ],
      },
      { path: '*', element: <Home /> },
    ],
  },
]);

export function App() {
  return (
    <ToastProvider>
      <RouterProvider router={router} />
    </ToastProvider>
  );
}
